import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-10" data-testid={`production-notes-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-[#E2E2DF] font-[Manrope]">{title}</h2>
    <div className="text-sm text-slate-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Code = ({ children }) => (
  <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-800">{children}</code>
);

const Pre = ({ children }) => (
  <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-md overflow-x-auto font-mono">{children}</pre>
);

export default function ProductionNotesPage() {
  return (
    <div data-testid="production-notes-page" className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Production Notes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Development history, architecture overview and hand-off instructions for this CRM.
          Intended for anyone (e.g. Trevor) cloning the repo into a new Emergent account.
        </p>
      </div>

      <Section title="Project Overview">
        <p>
          <strong>Roof&rsquo;s To Remedy Ltd CRM</strong> — a modern rebuild of the legacy
          MS Access database (<Code>RTR Database.mdb</Code>) into a full web application covering
          quoting, appraisal checklists, dynamic PDF reports, invoicing, GST, expense tracking,
          purchase orders, scheduling (Gantt), telemarketing, franchise management and a full
          roof-report builder.
        </p>
        <p>
          The app keeps legacy data and business logic intact while modernising the UI. It is multi-tenant:
          the master tenant is <em>Roof&rsquo;s To Remedy Ltd</em> and each franchise gets its own isolated MongoDB database.
        </p>
      </Section>

      <Section title="Tech Stack">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Frontend:</strong> React 19 + Tailwind + shadcn/ui + Recharts + react-pdf (PDF worker via <Code>unpkg.com</Code> CDN)</li>
          <li><strong>Backend:</strong> FastAPI (37 route modules) + ReportLab for PDFs</li>
          <li><strong>Database:</strong> MongoDB (48 collections, 267+ endpoints, 45 pages, 52 components)</li>
          <li><strong>Auth:</strong> JWT Bearer tokens stored in <Code>localStorage</Code> (NOT cookies)</li>
          <li><strong>Multi-tenancy:</strong> database-per-franchise with a <Code>contextvar</Code>-based DB proxy (<Code>core/database.py</Code>)</li>
        </ul>
      </Section>

      <Section title="Environment & Services">
        <ul className="list-disc pl-5 space-y-1">
          <li>Backend: internal port <Code>8001</Code>, all endpoints prefixed with <Code>/api</Code></li>
          <li>Frontend: port <Code>3000</Code>, calls backend via <Code>process.env.REACT_APP_BACKEND_URL</Code></li>
          <li>Mongo URL: <Code>MONGO_URL</Code> + <Code>DB_NAME</Code> in <Code>/app/backend/.env</Code></li>
          <li>Services are supervisor-managed with hot reload. Restart only on <Code>.env</Code> or dependency changes:
            <br /><Code>sudo supervisorctl restart backend</Code> / <Code>frontend</Code></li>
        </ul>
      </Section>

      <Section title="Key Feature Areas">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Clients / Properties / Leads / Appraisals</strong> — core CRM</li>
          <li><strong>Appraisal Components Form</strong> — quoting engine with dynamic rates; embedded tabs for Quote Comments and Roof Report</li>
          <li><strong>Roof Report Builder</strong> — 8 component tabs (Tiles, Sheeting, Ridging, Valleys, Barges, Spouting, Fastenings, Flashings) + defect quantities + Roof Condition &amp; Roof Requirements narrative tabs</li>
          <li><strong>Appraisal Conditions Library</strong> — categories: appraisal / roof / extra_work / roof_requirements (renamed from quote_comment)</li>
          <li><strong>Purchase Orders</strong> — auto-generated from quotes, grouped by supplier, email + partial delivery tracking</li>
          <li><strong>Invoices / Expenses / GST101A</strong> — NZ IRD aligned</li>
          <li><strong>Gantt Job Schedule</strong> — drag/resize/bulk</li>
          <li><strong>Task Catalog</strong> — 76 NZ tasks; <strong>Contractors</strong> (owner/operator)</li>
          <li><strong>Telemarketing Workspace</strong></li>
          <li><strong>Franchise Management</strong> — database-per-tenant; seeds 10 config collections + 15 indexes</li>
          <li><strong>Data Sync</strong> — 26 collections exported to CSV + Access blueprint (VBA macros) for legacy interop</li>
          <li><strong>Embedded User Manual</strong> — 24 sections, PDF via ReportLab</li>
        </ul>
      </Section>

      <Section title="Dynamic Quote Calculation">
        <p>
          Centralised math engine lives in <Code>/app/backend/core/quote_calc.py</Code>.
          Sheets use <Code>ceil(face/cover)</Code> logic. Per-component persisted fields:&nbsp;
          <Code>total</Code>, <Code>total_lm</Code>, <Code>total_m2</Code>, <Code>sheets_qty</Code>, <Code>cover</Code>,&nbsp;
          <Code>buy_total</Code>, <Code>mat_total</Code>, <Code>lab_total</Code>, <Code>con_total</Code>, <Code>rtr_profit</Code>.
        </p>
        <p>
          Per-appraisal persisted totals: <Code>calc_grand_total</Code>, <Code>calc_buy_total</Code>,&nbsp;
          <Code>calc_mat_total</Code>, <Code>calc_lab_total</Code>, <Code>calc_con_total</Code>,&nbsp;
          <Code>calc_rtr_profit</Code>, <Code>calc_last_computed_at</Code>.
        </p>
        <p>
          Auto-recalc runs after <Code>save-all</Code> component endpoint. Manual ops resync:&nbsp;
          <Code>POST /api/appraisals/{'{id}'}/rebuild-quote-items</Code> (routes through the component_lines gatekeeper).
        </p>
        <p>
          The bulk recalc has already been run across 4,710 legacy appraisals — aggregate quote value ≈ $3.99M,
          RTR profit ≈ $1.46M.
        </p>
      </Section>

      <Section title="Legacy Data Imported">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>17,474</strong> appraisal components (from 17 legacy tables → <Code>appraisal_components</Code>) via <Code>/app/backend/import_components_from_legacy.py</Code></li>
          <li><strong>64</strong> appraisal conditions (29 appraisal + 11 roof + 5 extra work + 19 quote comments → roof_requirements)</li>
          <li><strong>82</strong> roof report comments across 8 components</li>
          <li>Roof defect quantities seeded from <Code>AppraisalTbl.Defect*Qty</Code> columns (11 appraisals had data)</li>
          <li>Roof condition / requirements narratives seeded from legacy text fields (149 &amp; 22 appraisals)</li>
          <li>Roof types normalised across all libraries via <Code>/app/backend/normalize_roof_types.py</Code></li>
        </ul>
      </Section>

      <Section title="Key Collections">
        <ul className="list-disc pl-5 space-y-1">
          <li><Code>clients</Code>, <Code>properties</Code>, <Code>leads</Code>, <Code>appraisals</Code></li>
          <li><Code>appraisal_components</Code> — main quoting rows (component_type, item_type, profile, quantity, length, calc fields)</li>
          <li><Code>appraisal_condition_comments</Code> — narrative templates (categories + roof_type + effective_date)</li>
          <li><Code>roof_report_comments</Code> — per-component defect narratives</li>
          <li><Code>roof_reports</Code> — per-appraisal multi-select selections + defect_qty + roof_condition + roof_requirements</li>
          <li><Code>invoices</Code>, <Code>expenses</Code>, <Code>purchase_orders</Code>, <Code>inventory</Code></li>
          <li><Code>franchises</Code>, <Code>users</Code>, <Code>audit_log</Code></li>
        </ul>
      </Section>

      <Section title="Key API Endpoints">
        <ul className="list-disc pl-5 space-y-1">
          <li><Code>PATCH /api/appraisals/{'{id}'}/conditions</Code> — append a narrative block to an appraisal</li>
          <li><Code>GET /api/roof-reports/{'{id}'}</Code> &amp; <Code>PUT /api/roof-reports/{'{id}'}</Code></li>
          <li><Code>GET /api/search?q=</Code> — flattens clients / properties / appraisals / leads</li>
          <li><Code>GET /api/appraisal-conditions</Code>, <Code>POST/PUT/DELETE /api/appraisal-conditions/*</Code></li>
          <li><Code>GET /api/roof-report-comments</Code>, full CRUD alongside</li>
          <li><Code>POST /api/appraisals/{'{id}'}/rebuild-quote-items</Code> — ops resync via component_lines_gatekeeper</li>
          <li><Code>GET /api/data-sync/export</Code> — full CSV + Access blueprint bundle</li>
        </ul>
      </Section>

      <Section title="Known Conventions / Gotchas">
        <ul className="list-disc pl-5 space-y-1">
          <li>Always prefix backend routes with <Code>/api</Code> — the ingress routes <Code>/api</Code> → port 8001</li>
          <li>Frontend MUST call <Code>process.env.REACT_APP_BACKEND_URL</Code> — never hardcode localhost</li>
          <li>Always exclude <Code>_id</Code> from MongoDB responses (or convert ObjectId → str). Response models use Pydantic</li>
          <li>Auth: Bearer token in <Code>localStorage</Code> — no cookies, no refresh tokens</li>
          <li>Dates: <Code>datetime.now(timezone.utc)</Code>, ISO strings when serialising</li>
          <li>PDF worker stays on <Code>unpkg.com</Code> CDN — do not switch to a local build</li>
        </ul>
      </Section>

      <Section title="Cloning to a New Emergent Account (for Trevor)">
        <p>
          GitHub carries the <strong>code</strong> only — it does NOT carry MongoDB data. To move the populated legacy data
          (17k+ components, 4.7k appraisals, roof reports, condition libraries, rate tables) you need to run the export/import
          pair below. Keep the dumps private — they contain customer data.
        </p>
        <p><strong>1. On this (source) instance, export everything:</strong></p>
        <Pre>{`cd /app/backend
python scripts/export_mongo.py
# → writes one JSON file per collection to /app/backend/data_dumps/
# → also prints a manifest.json with counts`}</Pre>

        <p><strong>2. Commit the dumps (or zip + transfer securely) alongside the repo:</strong></p>
        <Pre>{`cd /app
tar -czf rtr_data_dumps.tar.gz backend/data_dumps
# hand this file to Trevor out-of-band`}</Pre>

        <p><strong>3. On Trevor&rsquo;s new Emergent instance, after cloning the repo:</strong></p>
        <Pre>{`# unpack dump
cd /app
tar -xzf rtr_data_dumps.tar.gz

# run import — uses MONGO_URL + DB_NAME from backend/.env
cd /app/backend
python scripts/import_mongo.py`}</Pre>

        <p>
          The import script is idempotent — it drops and reloads each collection, so re-running is safe.
          After import, refresh the browser and all legacy data will appear.
        </p>
      </Section>

      <Section title="Third-Party Integrations">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>SMTP / Gmail</strong> — required for Purchase Order emails. User must supply a Gmail App Password (Settings &gt; Company).</li>
          <li>No LLM, Stripe or payment integrations are wired in yet.</li>
        </ul>
      </Section>

      <Section title="Upcoming / Backlog">
        <ul className="list-disc pl-5 space-y-1">
          <li>E-signature on quotes (P1)</li>
          <li>Xero / QuickBooks accounting sync (P1)</li>
          <li>Auto-regenerate the <Code>.mdb</Code> legacy MS Access file on a schedule (P1)</li>
          <li>Product price history tracking (P2)</li>
          <li>Bulk pricing updates per supplier (P2)</li>
          <li>Weather-aware scheduling (P2)</li>
          <li>All items complete — no outstanding refactors on the Components/Legacy path.</li>
        </ul>
      </Section>

      <Section title="Files Worth Knowing">
        <ul className="list-disc pl-5 space-y-1">
          <li><Code>/app/frontend/src/pages/RoofReportBuilder.js</Code> — reusable roof-report UI</li>
          <li><Code>/app/frontend/src/components/layout/AppLayout.js</Code> — sidebar + global search</li>
          <li><Code>/app/backend/core/quote_calc.py</Code> — quote math engine</li>
          <li><Code>/app/backend/access_blueprint.py</Code> — VBA macros for Access DB creation</li>
          <li><Code>/app/backend/scripts/export_mongo.py</Code> + <Code>import_mongo.py</Code> — data handoff pair</li>
          <li><Code>/app/memory/PRD.md</Code> — detailed product requirement log</li>
        </ul>
      </Section>
    </div>
  );
}
